import React from 'react';
import { find } from 'lodash';
import moment from 'moment-timezone';
import {
  Button,
  Checkbox,
  Chip,
  TableCell,
  TableRow,
  Tooltip,
} from '@material-ui/core';

import { LinkTabs } from '../../../tab-table/tableView';
import LinkTable from '../../../tab-table/table';
import RequirementModal from './RequirementModal';

import { simpleNotification } from '../../../../utils/notifications';
import {
  getQuestRequirements,
  linkify,
  parseLinks,
  findDuplicateSubmissions,
  normalizeSubmissionUrl,
} from '../../../../utils/functions';
import { getContentMediaTypes } from '../../../../utils/apiv2';

import './QuestRequirementsTable.scss';

const notificationModule = (type) => {
  simpleNotification({
    level: 'warning',
    title: '',
    message: `Please select at least one item to ${type}.`,
  });
};

class QuestRequirementsTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 0,
      checkboxes: new Array((props.quest.requirements ? getQuestRequirements(props.quest, true).length : 0) * props.userQuests.length).fill(false),
      currentView: [],
      selected: [],
      contentTypes: {},
      // Tracks the last revoke response we've already notified on (see componentDidUpdate).
      lastRevokeSeq: (props.revokeResult && props.revokeResult.revokeSeq) || 0,
    };

    this.tabList = [
      { name: 'Pending', data: 'warning' },
      { name: 'Approved', data: 'completed' },
      { name: 'Rejected', data: 'error' },
    ];

    this.tabSections = [
      'warning',
      'completed',
      'error',
    ];
  }

  componentDidMount() {
    const { quest } = this.props;

    getContentMediaTypes(quest.id).then((res) => {
      const contentTypes = {};

      res.data.contentMediaTypes.forEach((type) => {
        contentTypes[type.value] = type.label;
      });

      this.setState({ contentTypes });
    });
  }

  // Surface the outcome of a revoke once its response lands (revokeSeq bumps per response).
  componentDidUpdate() {
    const result = this.props.revokeResult;
    if (!result || !result.revokeSeq || result.revokeSeq === this.state.lastRevokeSeq) return;

    // eslint-disable-next-line react/no-did-update-set-state
    this.setState({ lastRevokeSeq: result.revokeSeq });

    // simpleNotification clears prior notifications, so show a single combined message.
    if (result.revokedCount > 0 && result.revokeBlocked) {
      simpleNotification({
        level: 'warning',
        title: 'Revoke partially applied',
        message: `${result.revokedCount} submission(s) returned to the Pending queue. ${result.revokeBlocked}`,
        autoDismiss: 0,
      });
    } else if (result.revokedCount > 0) {
      simpleNotification({
        level: 'success',
        title: 'Approval revoked',
        message: `${result.revokedCount} submission(s) returned to the Pending queue.`,
      });
    } else if (result.revokeBlocked) {
      simpleNotification({
        level: 'warning',
        title: 'Could not revoke',
        message: result.revokeBlocked,
        autoDismiss: 0,
      });
    }
  }

  onCheck = (event, value, data) => {
    const { checkboxes } = this.state;
    let { selected } = this.state;

    if (value) selected.push(data);
    else selected = selected.filter(o => o.rowId !== data.rowId);

    checkboxes[data.rowId] = !checkboxes[data.rowId];

    this.setState({
      selected,
      checkboxes,
    });
  }

  setTab = (n) => {
    this.resetCheckBoxes();

    this.setState({
      tab: n,
      selected: [],
    });
  }

  resetCheckBoxes = () => {
    const { selected, checkboxes } = this.state;

    if (selected.length > 0) {
      selected.forEach((row) => { checkboxes[row.rowId] = false; });
    }

    this.setState({ selected: [], checkboxes });
  }

  selectAll = () => {
    const { currentView, checkboxes } = this.state;

    this.resetCheckBoxes();
    currentView.forEach((row) => { checkboxes[row.rowId] = true; });

    this.setState({ selected: currentView, checkboxes });
  }

  currentView = (rows) => {
    const { currentView } = this.state;

    if (currentView.length !== rows.length) {
      this.setState({ currentView: rows });
    } else {
      let same = true;

      const ids = rows.map(r => r.rowId);
      const currentIds = currentView.map(v => v.rowId);

      for (let i = 0; i < rows.length && same; i += 1) {
        if (!currentIds.includes(ids[i])) same = false;
      }

      if (!same) this.setState({ currentView: rows });
    }
  }

  isVOD = (requirement) => {
    const isVOD = !!((requirement['raw.type'] === 'submit-link' && requirement['raw.subtype'] === 'vod'));
    return isVOD;
  }

  rowMap = (tab, rows) => {
    const { selected, contentTypes } = this.state;
    const selectedUsernames = selected.length > 0 ? selected.map(s => s.username) : [];

    const newRows = rows.map((data) => {
      // Disable multi select across more than 1 user/1 VOD
      const notSelectedUser = !!((selectedUsernames.length > 0 && selectedUsernames[0] !== data.username));
      const anotherVODSelected = !!(this.isVOD(data) && (selected && selected.filter(s => (this.isVOD(s) && s['raw.id'] !== data['raw.id'])).length > 0));

      const disabled = anotherVODSelected || notSelectedUser;

      const checkbox = (
        <TableCell align="left">
          <Checkbox
            align="left"
            checked={this.state.checkboxes[data.rowId]}
            onChange={(e, value) => this.onCheck(e, value, data)}
            disabled={disabled}
          />
        </TableCell>
      );

      let display = null;
      const type = data['raw.type'];
      const { content } = data;

      if (content && (type === 'submit-tweet' || type === 'submit-link' || type === 'submit-content-media')) {
        display = linkify(content);
      } else if (content && (type === 'submit-text' || type === 'submit-text-optional')) {
        display = parseLinks(content);
      } else if (content) {
        display = content;
      }

      const reason = <TableCell align="left">{data.reason}</TableCell>;

      let submissionDate = '';
      if (data.submissionDate) {
        submissionDate = moment(data.submissionDate).tz('America/Los_Angeles').format('MMMM DD, YYYY - hh:mm a');
      }

      let rejectedDate = '';
      if (data.rejectedDate) {
        rejectedDate = moment(data.rejectedDate).tz('America/Los_Angeles').format('MMMM DD, YYYY - hh:mm a');
      }

      let completedDate = '';
      if (data.completedDate) {
        completedDate = moment(data.completedDate).tz('America/Los_Angeles').format('MMMM DD, YYYY - hh:mm a');
      }

      const rejectedDateCell = <TableCell align="left">{rejectedDate}</TableCell>;
      const completedDateCell = <TableCell align="left">{completedDate}</TableCell>;

      const title = type === 'submit-content-media' && !!data.contentType && !!contentTypes ? `[${data.title}] ${contentTypes[data.contentType]}` : data.title;

      // Flag submissions whose (normalized) link was also submitted by another user — a likely
      // duplicate / wrong submission. We surface it, we do NOT block it (admin decides).
      const duplicateBadge = (data.duplicateUsernames && data.duplicateUsernames.length > 0) ? (
        <Tooltip title={`Same link also submitted by: ${data.duplicateUsernames.join(', ')}`} placement="top">
          <Chip
            label={data.duplicateUsernames.length > 1 ? `Duplicate ×${data.duplicateUsernames.length}` : 'Duplicate'}
            size="small"
            style={{ backgroundColor: '#c0392b', color: 'white', marginLeft: '8px', fontWeight: 600 }}
          />
        </Tooltip>
      ) : null;

      const displayCell = (
        <TableCell align="left" style={{ whiteSpace: typeof display === 'string' ? 'pre-line' : undefined }}>
          {display}
          {duplicateBadge}
        </TableCell>
      );

      return (
        <TableRow key={data.rowId}>
          {(tab === 0 || tab === 1) ? checkbox : null}
          <TableCell align="left">{data.username}</TableCell>
          <TableCell align="left">{data.platformName}</TableCell>
          <TableCell align="left">{title}{ }</TableCell>
          {displayCell}
          <TableCell align="left">{submissionDate}</TableCell>
          {tab === 1 ? completedDateCell : null}
          {tab === 2 ? rejectedDateCell : null}
          {tab === 2 ? reason : null}
        </TableRow>
      );
    });

    return newRows;
  }

  completeSelected = (reason) => {
    if (this.state.selected.length <= 0) notificationModule('approve');
    else this.processRequirements(reason, 'completed');
  }

  rejectSelected = (reason, link = null) => {
    if (this.state.selected.length <= 0) notificationModule('reject');
    else this.processRequirements(reason, 'rejected');
  }

  // Un-approve the selected (already-approved) submissions, returning them to the Pending
  // queue. The backend refuses any that are locked by payment or a completion email and reports
  // them back via revokeResult (see componentDidUpdate).
  revokeSelected = () => {
    if (this.state.selected.length <= 0) { notificationModule('revoke'); return; }

    const { auth: { token }, revokeCCRequirement, quest: { id } } = this.props;

    const arrayToSend = this.state.selected.map(select => ({
      userId: select.userId,
      reqId: select['raw.id'],
      status: 'to-review',
    }));

    revokeCCRequirement(JSON.stringify(arrayToSend), id, token);
    this.resetCheckBoxes();
  }

  processRequirements = (reason, status) => {
    const { auth: { token }, postCCRequirementMultiple, quest: { id } } = this.props;
    const { selected } = this.state;

    const arrayToSend = [];

    for (let i = 0; i < selected.length; i += 1) {
      const select = selected[i];

      const newObj = {};

      newObj.userId = select.userId;
      newObj.content = select.content;
      newObj.id = select['raw.id'];
      newObj.note = reason || '';
      newObj.status = status;
      newObj.type = select['raw.type'];

      if (!newObj.status) {
        newObj.status = 'to-review';
      }

      const sendingObj = {
        ...newObj,
        resolution: !(reason),
        ...(reason ? { reason } : {}),
      };

      arrayToSend.push(sendingObj);
    }

    const uploadData = JSON.stringify(arrayToSend);

    postCCRequirementMultiple(uploadData, id, token);

    this.resetCheckBoxes();
  }

  render() {
    const { selected, tab, currentView } = this.state;
    const { userQuests, quest } = this.props;

    const requirements = getQuestRequirements(quest, true);

    // Map of normalizedUrl -> [{ userId, username }] for links submitted by 2+ distinct users.
    const duplicates = findDuplicateSubmissions(userQuests);

    const allUserReqs = [];
    let rowId = -1;

    for (let i = 0; i < userQuests.length; i += 1) {
      const uq = userQuests[i];

      if (uq.status === 'approved' || uq.status === 'normal') {
        const { user: { username, connectedAccounts, id } } = uq;
        const platformName = quest.twitchAccountRequired ? connectedAccounts.twitch.username : quest.youtubeAccountRequired ? connectedAccounts.youtube.username : 'NA';

        if (requirements && requirements.length > 0 && uq.requirements && uq.requirements.length > 0) {
          const userQuestRequirements = uq?.requirements || [];
          // eslint-disable-next-line no-loop-func
          requirements.forEach((r) => {
            // 过滤掉 submit-steam-achievement 类型的需求
            if (r.type === 'submit-steam-achievement') {
              return;
            }
            
            rowId += 1;
            let status = null;

            const ur = find(userQuestRequirements, o => o.questReqId === r.id);

            if (ur) {
              if (ur.status === 'completed') status = 'completed';
              else if (ur.status === 'rejected') status = 'error';
              else status = 'warning';

              let {
                submissionDate,
                rejectedDate,
                completedDate,
                content,
                contentType,
                views,
                likes,
                comments,
              } = ur;

              let title = 'error in finding title';
              if (r.title) title = r.title;
              else if (r.type === 'submit-tweet') title = 'Twitter Link';
              else if (r.type === 'submit-link' && r.subtype === 'vod') title = 'VOD Link';
              else if (r.type === 'submit-link' && r.subtype === 'clip') title = 'Clip Link';
              if (r.type === 'submit-video-youtube') content = `Youtube Video Link: ${content} \n Views: ${views} - Likes: ${likes} - Comments: ${comments}`;


              if (status === null) status = 'warning';

              // Other users in this quest who submitted the same (normalized) link.
              const dupKey = normalizeSubmissionUrl(ur.content || '');
              const duplicateUsernames = dupKey
                ? (duplicates[dupKey] || [])
                  .filter(u => String(u.userId) !== String(id))
                  .map(u => u.username)
                : [];

              const newRow = {
                rowId,
                username,
                platformName,
                title,
                status,
                content: content || '',
                contentType: contentType || null,
                submissionDate,
                rejectedDate,
                completedDate,
                reason: (ur && ur.note ? ur.note : null),
                raw: r,
                userId: id,
                duplicateUsernames,
              };

              allUserReqs.push(newRow);
            }
          });
        }
      }
    }

    const tabData = {
      warning: allUserReqs.filter(req => req.status === 'warning'),
      completed: allUserReqs.filter(req => req.status === 'completed'),
      error: allUserReqs.filter(req => req.status === 'error'),
    };

    const headers = [
      {
        id: 'username',
        numeric: false,
        label: 'NOIZ',
      },
      {
        id: 'platformName',
        numeric: false,
        label: (quest.twitchAccountRequired ?  'Twitch' : quest.youtubeAccountRequired ? 'Youtube' : 'NA'),
      },
      {
        id: 'title',
        numeric: false,
        label: 'Requirement',
      },
      {
        id: 'content',
        numeric: false,
        label: 'Content',
      },
      {
        id: 'submissionDate',
        numeric: false,
        label: 'Submission Date',
      },
    ];

    const blank = [{
      id: 'checkbox',
      numeric: false,
      label: '',
    }];

    const tabHeaders = {
      warning: blank.concat(headers),
      // Approved tab now carries a checkbox column too (so approvals can be revoked).
      completed: blank.concat(headers).concat([{ id: 'completedDate', numeric: false, label: 'Approved Date' }]),
      error: headers.concat([{ id: 'rejectedDate', numeric: false, label: 'Rejected Date' }, { id: 'reason', numeric: false, label: 'Reason' }]),
    };

    const tabRows = {
      warning: rows => this.rowMap(0, rows),
      completed: rows => this.rowMap(1, rows),
      error: rows => this.rowMap(2, rows),
    };

    const hasSelection = selected.length > 0;
    const canSelect = tab === 0 || tab === 1; // Pending or Approved
    const baseBtn = { variant: 'contained', size: 'large' };

    // Approve / Reject act on the Pending tab; Revoke acts on the Approved tab.
    const approveRejectProps = { ...baseBtn, ...((hasSelection && tab === 0) ? {} : { disabled: true }) };
    const revokeProps = { ...baseBtn, ...((hasSelection && tab === 1) ? {} : { disabled: true }) };
    const clearProps = { ...baseBtn, ...((hasSelection && canSelect) ? {} : { disabled: true }) };

    return (
      <div>
        <div className="SubmissionButtons">
          <RequirementModal
            title="Approve"
            handleSubmit={this.completeSelected}
            selectedCheckboxesLength={selected.length}
            useMaterialUI={approveRejectProps}
          />
          <RequirementModal
            title="Reject"
            handleSubmit={this.rejectSelected}
            selectedCheckboxesLength={selected.length}
            useMaterialUI={approveRejectProps}
          />
          <RequirementModal
            title="Revoke"
            handleSubmit={this.revokeSelected}
            selectedCheckboxesLength={selected.length}
            useMaterialUI={revokeProps}
          />
          <Tooltip title="Unselect all currently selected" placement="top">
            <span>
              <Button {...clearProps} onClick={this.resetCheckBoxes}>Clear Selected</Button>
            </span>
          </Tooltip>
          <Tooltip title="Select all requirements in the current viewing window" placement="top">
            <span>
              <Button {...baseBtn} disabled={!canSelect || currentView.length <= 0} onClick={this.selectAll}>Select All</Button>
            </span>
          </Tooltip>
        </div>
        <LinkTabs
          setTab={this.setTab}
          tabList={this.tabList}
          tableData={tabData}
          tableHeaders={this.tabList}
          headerTemplates={tabHeaders}
          rowTemplates={tabRows}
          Table={LinkTable}
          passUp={this.currentView}
        />
      </div>
    );
  }
}

export default QuestRequirementsTable;
